import { forwardRef } from "react";

import { isWeb } from "@memorang/helpers/platform";
import type { BaseItemSchema } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import { useDevice } from "@memorang/ui/useDevice";
import { useLocalSearchParams } from "expo-router";
import { FlatList, useWindowDimensions } from "react-native";

import StudySessionItemContainer from "../../containers/StudySessionItemContainer";
import SessionListDesktop from "./SessionListDesktop";

type Props = {
	items: BaseItemSchema[];
	toggleShowReadMore: () => void;
};

const StudySessionItemsLayout = forwardRef<FlatList<BaseItemSchema>, Props>(
	({ items, toggleShowReadMore }, ref) => {
		const { isMobile } = useDevice();
		const { width: windowWidth } = useWindowDimensions();
		const useFlatListPager = isMobile && !isWeb;
		const { index, id } = useLocalSearchParams<{
			index: string;
			id: string;
		}>();
		const currentItemIndex = index ? Number.parseInt(index) - 1 : 0;

		const renderItem = ({ item }: { item: BaseItemSchema }) => {
			return (
				<StudySessionItemContainer
					item={item}
					toggleShowReadMore={toggleShowReadMore}
					sessionId={id ? Number.parseInt(id) : undefined}
				/>
			);
		};

		if (!useFlatListPager) {
			return (
				<Box gap={16} flexGrow={1} alignItems="center" height="100%">
					<SessionListDesktop
						items={items}
						toggleShowReadMore={toggleShowReadMore}
					/>
				</Box>
			);
		}

		return (
			<FlatList
				renderItem={renderItem}
				data={items}
				ref={ref}
				initialScrollIndex={currentItemIndex}
				horizontal
				scrollEnabled={false}
				pagingEnabled
				keyboardShouldPersistTaps="always"
				keyExtractor={(item) => `${item.id}`}
				getItemLayout={(_data, index) => ({
					length: windowWidth,
					offset: windowWidth * index,
					index,
				})}
			/>
		);
	},
);

StudySessionItemsLayout.displayName = "StudySessionItemsLayout";

export default StudySessionItemsLayout;
